import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { RepoTicketGroup, RepoTicketsResponse } from '@clearflow/shared';
import { api } from '../../lib/api.ts';

// Cible générique de la tooltip au survol (JiraTicketTooltip) — un ticket de la liste (branches
// non vide) ou une subtask dépliée (pas de branche : une subtask n'est jamais référencée par une
// branche elle-même). Type dédié plutôt que RepoTicketGroup directement : une subtask n'a pas de
// `hasSubtasks`/`branches` propres, seulement ce que la tooltip affiche réellement.
export interface JiraTooltipTarget {
  ticketKey: string;
  url: string | null;
  summary: string | null;
  branches: string[];
}

interface Props {
  tickets: RepoTicketsResponse;
  // Le positionnement (ancrage sur le bord de la sidebar, timer de fermeture différée) vit dans
  // RepoViewPage.tsx, même mécanique que les lignes mr-row/cherry-pick inline là-bas — cette
  // liste-ci est un composant séparé, donc ce sont ces deux callbacks qui remontent le survol
  // plutôt que d'inline la logique ici.
  onHoverTicket: (ticket: JiraTooltipTarget, rowRect: DOMRect) => void;
  onLeaveTicket: () => void;
}

// Liste, pour la sidebar "Tickets Jira" (RepoViewPage.tsx), les tickets référencés par au moins
// une branche du repo — voir resolveRepoTickets côté service pour la clé/branches (aucun appel
// Jira) et resolveRepoTicketSummaries pour le titre (un appel par ticket unique, tolérant à
// l'échec individuel — voir packages/shared/src/jira.ts). `summary` null : pas d'appel raté
// silencieux, juste pas de ligne de titre pour ce ticket-là (ticket supprimé, inaccessible…).
export function RepoJiraTickets({ tickets, onHoverTicket, onLeaveTicket }: Props) {
  if (tickets.length === 0) {
    return <p className="muted" style={{ fontSize: 12, margin: 0 }}>Aucun ticket référencé par une branche.</p>;
  }
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: 12 }}>
      {tickets.map((t) => (
        <JiraTicketRow key={t.ticketKey} ticket={t} onHoverTicket={onHoverTicket} onLeaveTicket={onLeaveTicket} />
      ))}
    </ul>
  );
}

interface RowProps {
  ticket: RepoTicketGroup;
  onHoverTicket: (ticket: JiraTooltipTarget, rowRect: DOMRect) => void;
  onLeaveTicket: () => void;
}

// Une seule ligne de la liste — composant séparé (plutôt qu'inline dans le .map ci-dessus) pour
// que chaque ticket ait son propre state `expanded` et son propre useQuery, sans casser les
// règles des hooks React (nombre de hooks par instance de composant, pas par itération de map).
// Les subtasks ne sont connues qu'après un getIssue complet (même detail que JiraTicketTooltip) —
// impossible de savoir à l'avance si un ticket en a avant d'avoir cliqué dessus une première fois ;
// le clic déclenche donc à la fois le fetch et le dépliage (staleTime partagé avec la tooltip via
// la même queryKey : un survol préalable évite un second aller-retour réseau au clic).
function JiraTicketRow({ ticket, onHoverTicket, onLeaveTicket }: RowProps) {
  const [expanded, setExpanded] = useState(false);
  const detailQuery = useQuery({
    queryKey: ['jira-ticket-detail', ticket.ticketKey],
    queryFn: () => api.ticketDetail(ticket.ticketKey),
    enabled: expanded,
    staleTime: 15_000,
  });
  const subtasks = detailQuery.data?.subtasks ?? [];

  return (
    <li
      className="jira-ticket-row"
      role="button"
      tabIndex={0}
      onClick={() => setExpanded((v) => !v)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          setExpanded((v) => !v);
        }
      }}
    >
      <div
        className="row jira-ticket-header"
        style={{ gap: 6, alignItems: 'baseline' }}
        // Handlers de survol posés ici plutôt que sur le <li> englobant : le <li>, une fois déplié,
        // contient aussi la liste de subtasks (chacune avec ses propres handlers) — mouseenter/
        // mouseleave ne bullent pas, donc les poser sur le <li> masquerait tout changement de
        // tooltip quand la souris passe du ticket à l'une de ses subtasks (on resterait "dans" le
        // <li> sans jamais retraverser sa frontière). Ici, ce sont deux zones sœurs indépendantes.
        onMouseEnter={(e) =>
          onHoverTicket(
            { ticketKey: ticket.ticketKey, url: ticket.url, summary: ticket.summary, branches: ticket.branches },
            e.currentTarget.getBoundingClientRect(),
          )
        }
        onMouseLeave={onLeaveTicket}
      >
        {ticket.url ? (
          <a
            className={`jira-ticket-badge${ticket.isDone ? ' jira-ticket-badge-done' : ''}`}
            href={ticket.url}
            target="_blank"
            rel="noreferrer"
            title={`Ouvrir ${ticket.ticketKey} dans un nouvel onglet`}
            onClick={(e) => e.stopPropagation()}
          >
            {ticket.ticketKey}
          </a>
        ) : (
          <span className={`jira-ticket-badge${ticket.isDone ? ' jira-ticket-badge-done' : ''}`}>{ticket.ticketKey}</span>
        )}
        {/* Distinguable d'un coup d'œil sans ouvrir la tooltip : pastille "version" pleine, absente
            des tickets sans fix version (RepoTicketGroup.hasFixVersions). */}
        {ticket.hasFixVersions && (
          <span className="jira-fixversion-indicator" title="Ce ticket a au moins une fix version">
            <svg viewBox="0 0 16 16" width="11" height="11" fill="currentColor" aria-hidden="true">
              <path d="M2 2h6l6 6-6 6-6-6V2zm3 2a1 1 0 100 2 1 1 0 000-2z" />
            </svg>
          </span>
        )}
        {ticket.hasSubtasks && (
          <span className="jira-subtask-indicator" title="Ce ticket a des sous-tâches">
            <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <path d="M3 4h10M3 8h10M3 12h6" />
            </svg>
          </span>
        )}
        {/* Pas de `title` ici : ferait doublon avec la tooltip riche (JiraTicketTooltip) déjà
            déclenchée par onMouseEnter ci-dessus — deux tooltips superposées au survol, l'une
            (native) réduite au seul titre. */}
        {ticket.summary && (
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {ticket.summary}
          </span>
        )}
      </div>
      {expanded && subtasks.length > 0 && (
        <ul className="jira-subtask-list">
          {subtasks.map((s) => (
            <li
              key={s.key}
              className="jira-subtask-row"
              // Une subtask est un descendant du <li> ticket (onClick={() => setExpanded(...)},
              // maintenant aussi onKeyDown Enter/Espace) — sans ces stopPropagation, cliquer ou
              // activer au clavier son résumé (le lien a déjà le sien) referme le ticket au lieu
              // de ne rien faire.
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onMouseEnter={(e) =>
                onHoverTicket(
                  { ticketKey: s.key, url: s.url, summary: s.summary, branches: [] },
                  e.currentTarget.getBoundingClientRect(),
                )
              }
              onMouseLeave={onLeaveTicket}
            >
              <a
                className={`jira-ticket-badge${s.isDone ? ' jira-ticket-badge-done' : ''}`}
                href={s.url}
                target="_blank"
                rel="noreferrer"
                title={`Ouvrir ${s.key} dans un nouvel onglet`}
                onClick={(e) => e.stopPropagation()}
              >
                {s.key}
              </a>
              {/* Idem : pas de `title`, la tooltip riche (déclenchée par onMouseEnter du <li>
                  ci-dessus) suffit déjà. */}
              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.summary}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
